import React from 'react';
import { Project } from '../../types';
import { formatCurrencyCompact } from '../../lib/format/currency';
import { getEffectivePopulation } from '../../utils/population';
import { getEffectiveDistance } from '../../utils/distance';
import { calculateTotalRidership, formatNumber } from '../../utils/ridership';
import { calculateNetIncomePercentage, formatPercentage, getPercentageColor } from '../../utils/finance';

interface Props {
  project: Project;
  matrices: {
    distances: number[][];
    gravity: number[][];
    ridership: number[][];
    trainsPerDay: number[][];
    passengerKm: number[][];
    fares: number[][];
    revenue: number[][];
    adjacency: number[][];
    constructionCost: number[][];
    passengersBySegment: number[][];
  };
}

export const SummaryView: React.FC<Props> = ({ project, matrices }) => {
  // Network overview calculations
  const totalPopulation = project.network.stations.reduce(
    (sum, station) => sum + getEffectivePopulation(station),
    0
  );

  const totalNetworkLength = matrices.adjacency.reduce(
    (sum, row) => sum + row.reduce((rowSum, cell) => rowSum + cell, 0),
    0
  ) / 2;

  const totalConstructionCost = totalNetworkLength * project.config.costs.constructionCostPerKm;

  // Ridership overview calculations
  const totalRiders = calculateTotalRidership(matrices);

  const totalPassengerKm = matrices?.passengerKm?.reduce(
    (sum, row) => sum + row.reduce((rowSum, cell) => rowSum + cell, 0),
    0
  ) / 2 || 0;

  const passengersPerKm = totalNetworkLength > 0
    ? totalRiders / totalNetworkLength
    : 0;

  const totalTrainsPerDay = matrices?.trainsPerDay?.reduce(
    (sum, row) => sum + row.reduce((rowSum, cell) => rowSum + cell, 0),
    0
  ) / 2 || 0;

  // Financial overview calculations
  const revenue = matrices?.revenue?.reduce(
    (sum, row) => sum + row.reduce((rowSum, cell) => rowSum + cell, 0),
    0
  ) / 2 || 0;

  const operatingCosts = totalPassengerKm * project.config.costs.operatingCostPerKm;

  const { duration, interestRate } = project.config.bond;
  const r = interestRate / 100;
  const bondPayments = totalConstructionCost * r * Math.pow(1 + r, duration) / (Math.pow(1 + r, duration) - 1);

  const netIncome = revenue - operatingCosts - bondPayments;
  const profitLossPercentage = calculateNetIncomePercentage(revenue, netIncome);

  // Log raw summary statistics
  console.log('Summary Statistics (Raw Values):', {
    network: {
      totalPopulation,
      totalNetworkLength,
      totalConstructionCost
    },
    ridership: {
      totalRiders,
      totalPassengerKm,
      passengersPerKm,
      totalTrainsPerDay
    },
    finances: {
      revenue,
      operatingCosts,
      bondPayments,
      netIncome,
      profitLossPercentage
    }
  });

  const formatCurrency = (value: number): string => {
    return formatCurrencyCompact(value, project.config.bond.currency);
  };

  return (
    <div className="px-3 py-3 w-full max-w-6xl mx-auto mb-24">
      <div className="space-y-8 md:space-y-12">
        {/* Network Overview */}
        <section className="pt-0 sm:pt-3 md:pt-5">
          <h2 className="text-lg sm:text-xl font-medium text-[#4A4538] mb-4 sm:mb-6">Network Overview: {project.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Total Population</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatNumber(totalPopulation)}</p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Network Length</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatNumber(totalNetworkLength)} km</p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Construction Cost</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatCurrency(totalConstructionCost)}</p>
            </div>
          </div>
        </section>

        {/* Ridership Overview */}
        <section>
          <h2 className="text-lg sm:text-xl font-medium text-[#4A4538] mb-4 sm:mb-6">Annual Ridership</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Passengers</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatNumber(totalRiders)}</p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Passenger-Kilometers</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatNumber(totalPassengerKm)}</p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Passengers per rail-km</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatNumber(passengersPerKm)}</p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Trains per day</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatNumber(totalTrainsPerDay)}</p>
            </div>
          </div>
        </section>

        {/* Financial Overview */}
        <section>
          <h2 className="text-lg sm:text-xl font-medium text-[#4A4538] mb-4 sm:mb-6">Annual Finances</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Revenue</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatCurrency(revenue)}</p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Operating Costs</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatCurrency(operatingCosts)}</p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Construction Loan Payment</h3>
              <p className="text-xl sm:text-2xl text-[#6A8CAF] font-mono">{formatCurrency(bondPayments)}</p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow lg:col-span-2">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Net Income</h3>
              <p className={`text-xl sm:text-2xl font-mono ${getPercentageColor(netIncome)}`}>
                {formatCurrency(netIncome)}
              </p>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-[#4A4538] mb-2">Profit/Loss</h3>
              <p className={`text-xl sm:text-2xl font-mono ${getPercentageColor(profitLossPercentage)}`}>
                {formatPercentage(profitLossPercentage)}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}; 